import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import type { CreditCheckRequest, CreditDecisionResponse } from '@rheora/enrollment-contract';

import { postCreditCheck } from './credit.fetchers';
import { funnelQueryKeys } from './funnel.queries';

/**
 * TanStack hook for the E-SF05 credit check. Pages call THIS, never the fetcher.
 *
 * ── WHY A MUTATION, NOT A QUERY (this matters for SSN) ─────────────────────
 * The credit check is a POST with side effects (a bureau pull + a funnel-state
 * write) and an idempotency key — a mutation. It is ALSO the right choice for the
 * SSN: a `useQuery` would put the request (SSN included) in the QUERY KEY, which
 * TanStack persists in its cache. A mutation passes the request as VARIABLES,
 * which are never keyed or cached. The SSN therefore never enters the query cache.
 *
 * ── ON SUCCESS IT INVALIDATES THE FUNNEL SNAPSHOT, NOT THE REQUEST ─────────
 * The credit check creates the Accounts prospect and may set the DEPOSIT_PENDING
 * gate, so the cached `GET /bff/funnel` snapshot is stale the moment it returns.
 * ⚠️ The invalidation carries NOTHING from the request: `funnelQueryKeys.snapshot()`
 * is a constant tuple, so the SSN cannot reach a cache key through this door either.
 */
export function useCreditCheckMutation(): UseMutationResult<
  CreditDecisionResponse,
  Error,
  CreditCheckRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postCreditCheck,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: funnelQueryKeys.snapshot() }),
  });
}
